import { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { ChevronDown, X, Search } from 'lucide-react';

/**
 * Autocomplete select component for projects
 * Supports filtering by typing
 */
function ProjectAutocomplete({ 
  projects = [], 
  value = '', 
  onChange, 
  placeholder = 'Seleziona progetto...',
  allowAll = true,
  className = ''
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Filter projects based on search term
  const filteredProjects = projects.filter(project => {
    const name = typeof project === 'string' ? project : project.name;
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Get display name for a project
  const getProjectName = (project) => {
    return typeof project === 'string' ? project : project.name;
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (projectValue) => {
    onChange(projectValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  const displayValue = value || (allowAll ? 'Tutti i progetti' : placeholder);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger button */}
      <div
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            setTimeout(() => inputRef.current?.focus(), 100);
          }
        }}
        className="flex items-center justify-between px-3 py-2 bg-[rgb(39,39,42)] border border-[rgb(63,63,70)] rounded-md cursor-pointer hover:border-[rgb(82,82,91)] transition-colors min-h-[40px]"
      >
        <span className={`font-mono text-sm truncate ${value ? 'text-[rgb(250,250,250)]' : 'text-[rgb(161,161,170)]'}`}>
          {displayValue}
        </span>
        <div className="flex items-center gap-1">
          {value && (
            <button
              onClick={handleClear}
              className="p-1 hover:bg-[rgb(63,63,70)] rounded"
            >
              <X className="h-3 w-3 text-[rgb(161,161,170)]" />
            </button>
          )}
          <ChevronDown className={`h-4 w-4 text-[rgb(161,161,170)] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-[rgb(39,39,42)] border border-[rgb(63,63,70)] rounded-md shadow-lg max-h-[300px] overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-[rgb(63,63,70)]">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-[rgb(161,161,170)]" />
              <Input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cerca progetto..."
                className="pl-8 bg-[rgb(24,24,27)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono text-sm"
              />
            </div>
          </div>

          {/* Options list */}
          <div className="max-h-[240px] overflow-y-auto">
            {allowAll && (
              <div
                onClick={() => handleSelect('')}
                className={`px-3 py-2 cursor-pointer hover:bg-[rgb(63,63,70)] font-mono text-sm ${
                  !value ? 'bg-[rgb(59,130,246)]/20 text-[rgb(59,130,246)]' : 'text-[rgb(250,250,250)]'
                }`}
              >
                Tutti i progetti
              </div>
            )}
            {filteredProjects.length === 0 ? (
              <div className="px-3 py-4 text-center text-[rgb(161,161,170)] font-mono text-sm">
                Nessun progetto trovato
              </div>
            ) : (
              filteredProjects.map((project, index) => {
                const name = getProjectName(project);
                return (
                  <div
                    key={index}
                    onClick={() => handleSelect(name)}
                    className={`px-3 py-2 cursor-pointer hover:bg-[rgb(63,63,70)] font-mono text-sm ${
                      value === name ? 'bg-[rgb(59,130,246)]/20 text-[rgb(59,130,246)]' : 'text-[rgb(250,250,250)]'
                    }`}
                  >
                    {name}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectAutocomplete;
